import { normalizeGatewayMacHex, resolveGatewayMqttTopics } from './gatewayMqttTopics';
import { getGatewayAdminCapabilities } from './gatewayAdminCapabilities';

const FILTER_MODE_ITEMS = [
  { id: 'off', label: 'Off — report all' },
  { id: 'name', label: 'Name — regex on BLE name' },
  { id: 'mac', label: 'MAC — allowlist / prefix' },
  { id: 'both', label: 'Both name and MAC' },
];

export { FILTER_MODE_ITEMS };

export function configSourceLabel(source) {
  switch (source) {
    case 'mqtt':
      return 'Live from gateway (MQTT getCfg)';
    case 'cache':
    case 'db':
      return 'Last saved configuration (offline copy)';
    default:
      return 'Defaults — gateway did not respond';
  }
}

/**
 * Parse getCfg / last_known_config into list-modal form fields.
 * Field names follow KGateway admin payloads; ESP LilyGO mirrors the same keys.
 */
export function parseGatewayConfigFields(config, gateway) {
  const caps = getGatewayAdminCapabilities(gateway);
  const raw = config && typeof config === 'object' ? config : {};
  const ctype = String(raw.ctype || 'MQTT').toUpperCase();

  const uploadInterval = parseInt(
    ctype === 'HTTP'
      ? (raw.httpPeriod || raw.http_period || 60)
      : (raw.rptPeriod || raw.ingest_interval_sec || raw.scan_interval_sec || 120),
    10,
  );

  const filterName = caps.isKGateway
    ? (raw.filterdata1 || raw.filtername || '')
    : (raw.filtername || raw.filterdata1 || '');

  return {
    connectivityType: ctype === 'HTTP' ? 'http' : 'mqtt',
    uploadInterval: Number.isFinite(uploadInterval) && uploadInterval > 0 ? uploadInterval : 120,
    filterRssi: raw.filterrssi ?? raw.filter_rssi ?? -100,
    filterMac: raw.filtermac || raw.mac_allowlist || '',
    filterName: String(filterName || ''),
    filterMode: raw.filter_mode || 'off',
    firmwareVersion: raw.ver || raw.firmware_version || '',
    scanTimeSec: raw.scan_time_sec ?? null,
    minPushGapSec: raw.min_push_gap_sec ?? null,
  };
}

export function getDefaultSettingsForm(gateway) {
  const caps = getGatewayAdminCapabilities(gateway);
  return {
    connectivityType: caps.isKGateway ? 'mqtt' : 'mqtt',
    uploadInterval: 120,
    filterRssi: -100,
    filterMac: '',
    filterName: '',
    filterMode: 'off',
    firmwareVersion: '',
    scanTimeSec: null,
    minPushGapSec: null,
  };
}

/**
 * Build stype cfg / http command for the gateway list settings modal.
 */
export function buildSettingsSaveCommand(form, gateway) {
  const caps = getGatewayAdminCapabilities(gateway);
  const macHex = normalizeGatewayMacHex(gateway);
  const mqttTopics = resolveGatewayMqttTopics(gateway);
  const seq = Math.floor(Date.now() / 1000);

  if (caps.isKGateway && form.connectivityType === 'http') {
    const httpUrl = gateway.http_endpoint || '';
    const httpHeader = gateway.http_auth_mode === 'basic'
      ? `Authorization: Basic ${btoa(`${gateway.http_username || ''}:${gateway.http_password || ''}`)}`
      : gateway.http_api_key_name && gateway.http_api_key_value
        ? `${gateway.http_api_key_name}: ${gateway.http_api_key_value}`
        : gateway.http_api_key
          ? `Authorization: ${gateway.http_api_key}`
          : '';

    return {
      msg: 'admin',
      stype: 'http',
      ctype: 'HTTP',
      seq,
      user: 'admin',
      pwd: 'admin',
      httpUrl,
      httpHeader,
      httpMaxPktSize: 100,
      httpPeriod: String(form.uploadInterval),
    };
  }

  const broker = gateway.mqtt_broker_host || '';
  const port = gateway.mqtt_broker_port ?? 1883;
  const mqttUser = gateway.mqtt_username || gateway.tbmq_username || '';
  const mqttPwd = gateway.mqtt_password || gateway.tbmq_password || '';

  const command = {
    msg: 'admin',
    stype: 'cfg',
    ctype: 'MQTT',
    seq,
    user: 'admin',
    pwd: 'admin',
    murl: broker,
    mport: String(port),
    username: mqttUser,
    password: mqttPwd,
    clientid: gateway.mqtt_client_id || `gateway_${macHex}_${Date.now()}`,
    pubtopic: gateway.mqtt_pub_topic || gateway.mqtt_topic || mqttTopics.publishTopic,
    subtopic: gateway.mqtt_sub_topic || mqttTopics.subActionTopic,
    pubaction: mqttTopics.pubActionTopic,
    subadmintopic: gateway.mqtt_admin_sub_topic || mqttTopics.subAdminTopic,
    pubadmintopic: gateway.mqtt_admin_pub_topic || mqttTopics.pubAdminTopic,
    qos: '0',
    sqos: '0',
    aliveTopic: '0',
    rptPeriod: String(form.uploadInterval),
    filterrssi: String(form.filterRssi),
  };

  if (form.filterMac) command.filtermac = form.filterMac;
  if (caps.isKGateway) {
    if (form.filterName) command.filterdata1 = form.filterName;
  } else {
    command.ingest_interval_sec = form.uploadInterval;
    if (form.filterName) command.filtername = form.filterName;
    if (form.filterMode) command.filter_mode = form.filterMode;
  }

  return command;
}

/** Merge form edits into a config object for last_known_config persistence. */
export function mergeFormIntoConfig(baseConfig, form, gateway) {
  const caps = getGatewayAdminCapabilities(gateway);
  const merged = { ...(baseConfig && typeof baseConfig === 'object' ? baseConfig : {}) };

  if (caps.isKGateway && form.connectivityType === 'http') {
    merged.ctype = 'HTTP';
    merged.httpPeriod = String(form.uploadInterval);
    return merged;
  }

  merged.ctype = 'MQTT';
  merged.rptPeriod = String(form.uploadInterval);
  merged.ingest_interval_sec = form.uploadInterval;
  merged.filterrssi = form.filterRssi;
  merged.filtermac = form.filterMac;
  if (caps.isKGateway) {
    merged.filterdata1 = form.filterName;
  } else {
    merged.filtername = form.filterName;
    merged.filter_mode = form.filterMode;
  }
  if (form.firmwareVersion) merged.ver = form.firmwareVersion;
  return merged;
}
